import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';

import { queryClient } from 'api';
import { EditableSession } from 'types/event.types';
import { isEventStartingInOneHour } from 'utils/eventUtils';
import { GuestSideSheet } from 'components/dashboard/EventInsights/EventInsightsGuestList/GuestSideSheet/GuestSideSheet';
import { Button } from '@epoch-ui/components/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '@epoch-ui/components/Table/table';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { Icon } from '@epoch-ui/components/Icon';
import { SearchInput } from '@epoch-ui/components/SearchInput';
import Pagination from '@epoch-components/tables/Pagination';
import { eventGuestsApi } from '@event-editor/api/event-guests';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { PLUS_ONE_GUESTS } from 'constants/featureFlags';

import { guestListColumnsFactory, formatFilterStatuses, formatDepartmentsFilter } from './utils';
import { GuestListFiltersSheet, GuestListFilters } from './GuestListFiltersSheet';

const PAGE_SIZE = 10;

const GuestsList: React.FC<{ session?: EditableSession }> = ({ session }) => {
  const event = useEditableEvent();
  const eventOrSession = session ?? event;
  const plusOneEnabled = !!useFeatureFlag(PLUS_ONE_GUESTS);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGid, setSelectedGid] = useState<string | null>(null);
  const [selectedPlusOneId, setSelectedPlusOneId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>();
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<GuestListFilters>({
    statuses: [],
    attendance: [],
    departments: []
  });

  const {
    data: guestsData,
    isLoading,
    isFetching
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- plusOneEnabled only affects which backend query runs, not cache identity
    queryKey: eventEditorQueryKeys.eventGuestsList(eventOrSession.id!, {
      currentPage,
      pageSize: PAGE_SIZE,
      searchText,
      statuses: selectedFilters.statuses,
      attendanceFilters: selectedFilters.attendance,
      departments: selectedFilters.departments
    }),
    queryFn: () =>
      eventGuestsApi.listGuests({
        eventId: eventOrSession.id!,
        currentPage,
        pageSize: PAGE_SIZE,
        searchText,
        statuses: selectedFilters.statuses?.map(formatFilterStatuses),
        attendanceFilters: selectedFilters.attendance,
        departments: selectedFilters.departments?.map(formatDepartmentsFilter),
        isPlusOneEnabled: plusOneEnabled
      }),
    enabled: event.isPublished && (event.totalGuests ?? 0) > 0,
    placeholderData: (previousData) => previousData
  });

  const guests = guestsData?.guests || [];
  const totalPages = guestsData?.pages || 0;

  const totalSelectedFilters = useMemo(() => {
    return Object.values(selectedFilters).flat().length;
  }, [selectedFilters]);

  const shouldShowAttendanceColumn =
    isEventStartingInOneHour(eventOrSession) && !eventOrSession.isUsingEstimatedAttendees;

  const handleNameClick = (gid: string, plusOneId?: number | null): void => {
    setSelectedGid(gid);
    setSelectedPlusOneId(plusOneId ?? null);
  };

  const columns = guestListColumnsFactory({
    onNameClick: handleNameClick,
    shouldShowAttendanceColumn
  });

  const table = useReactTable({
    data: guests,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const afterGuestUpdateHandler = useCallback(() => {
    // Guests table
    queryClient.invalidateQueries({
      queryKey: eventEditorQueryKeys.eventGuestsList(eventOrSession.id!, {
        currentPage,
        pageSize: PAGE_SIZE,
        searchText,
        statuses: selectedFilters.statuses,
        attendanceFilters: selectedFilters.attendance,
        departments: selectedFilters.departments
      })
    });

    // Guests summary
    queryClient.invalidateQueries({
      queryKey: eventEditorQueryKeys.eventGuestStats(eventOrSession.id!)
    });

    // Questionnaire responses
    queryClient.invalidateQueries({
      queryKey: eventEditorQueryKeys.eventQuestionnaires
    });
  }, [eventOrSession.id, currentPage, searchText, selectedFilters]);

  return (
    <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-4">
      {/* Table filters */}
      <div className="tw-flex tw-gap-2">
        <SearchInput
          placeholder="Search"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value.trim().length > 0 ? e.target.value : undefined);
          }}
          debounce={500}
        />
        <Button
          variant={totalSelectedFilters > 0 ? 'primary' : 'secondary'}
          size="sm"
          className="tw-rounded-[100px]"
          onClick={() => setFiltersSheetOpen(true)}
        >
          Filters {totalSelectedFilters > 0 ? `(${totalSelectedFilters})` : ''}
          <Icon name="list-filter" size={16} className="tw-ml-2" />
        </Button>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="tw-border-0 tw-border-b tw-border-solid tw-border-border"
            >
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="tw-py-3">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading && !isFetching && guests.length === 0
            ? // Show skeleton rows for first load
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.id} className="tw-py-4">
                      <Skeleton className="tw-h-8 tw-w-3/4" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => (
                // Guest data rows
                <TableRow
                  key={row.id}
                  className="tw-border-0 tw-border-b tw-border-solid tw-border-border"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="tw-py-4">
                      {isFetching ? (
                        <Skeleton className="tw-h-8 tw-w-3/4" />
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <Pagination
          perPage={guestsData?.perPage || PAGE_SIZE}
          currPage={currentPage}
          totalCount={guestsData?.totalGuests || 0}
          onPageChange={setCurrentPage}
        />
      )}

      <GuestListFiltersSheet
        open={filtersSheetOpen}
        onOpenChange={setFiltersSheetOpen}
        initialFilters={selectedFilters}
        onApply={(filters) => {
          setSelectedFilters(filters);
          setFiltersSheetOpen(false);
        }}
      />

      <GuestSideSheet
        key={`${selectedGid}-${selectedPlusOneId}`}
        eventOrSessionId={eventOrSession.id!}
        gid={selectedGid}
        handleUpdateRefetch={afterGuestUpdateHandler}
        initialPlusOneId={selectedPlusOneId}
        open={selectedGid !== null}
        onOpenChange={(open): void => {
          if (!open) {
            setSelectedGid(null);
            setSelectedPlusOneId(null);
          }
        }}
      />
    </div>
  );
};

export { GuestsList };
